import React, { memo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Search, Tag, Clock, Users, ArrowRight, Sparkles,
  Play, Eye, Copy, Download
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { flowTemplates, templateCategories, FlowTemplate } from '../data/flowTemplates';
import useFlowStore from '../store/flowStore';
import toast from 'react-hot-toast';

interface TemplateGalleryProps {
  isOpen: boolean;
  onClose: () => void;
}

const TemplateGallery: React.FC<TemplateGalleryProps> = memo(({ isOpen, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<FlowTemplate | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  
  const store = useFlowStore();
  
  const filteredTemplates = flowTemplates.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });
  
  const handleUseTemplate = (template: FlowTemplate) => {
    // Adjust node positions to center them
    const offsetX = 400;
    const offsetY = 100;
    
    const adjustedNodes = template.nodes.map(node => ({
      ...node,
      position: {
        x: node.position.x + offsetX,
        y: node.position.y + offsetY
      }
    }));
    
    // Clear existing flow and load template
    store.setNodes([]);
    store.setEdges([]);
    
    // Add template nodes and edges
    adjustedNodes.forEach(node => store.addNode(node));
    template.edges.forEach(edge => store.setEdges([...store.edges, edge]));
    
    toast.success(`Template "${template.name}" caricato!`, {
      icon: '✨',
      style: {
        borderRadius: '10px',
        background: '#333',
        color: '#fff',
      },
    });
    
    onClose();
  };
  
  const handleDuplicate = (template: FlowTemplate) => {
    const duplicatedTemplate = {
      ...template,
      id: `${template.id}-copy-${Date.now()}`,
      name: `${template.name} (Copia)`
    };
    
    // In a real app, you would save this to the templates list
    toast.success('Template duplicato!', {
      icon: '📋',
      style: {
        borderRadius: '10px',
        background: '#333',
        color: '#fff',
      },
    });
  };
  
  const renderTemplateCard = (template: FlowTemplate) => (
    <motion.div
      key={template.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      onClick={() => setSelectedTemplate(template)}
      className="bg-gray-800 rounded-xl p-5 cursor-pointer hover:bg-gray-700 transition-all border border-gray-700 hover:border-whatsapp-green group"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{template.icon}</span>
          <div>
            <h3 className="text-white font-medium">{template.name}</h3>
            <p className="text-xs text-gray-400">{template.category}</p>
          </div>
        </div>
        <ArrowRight className="text-gray-400 group-hover:text-whatsapp-green transition-colors" size={20} />
      </div>
      
      {/* Description */}
      <p className="text-sm text-gray-300 mb-3">{template.description}</p>
      
      {/* Stats */}
      <div className="flex items-center gap-4 mb-3">
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <Users size={14} />
          <span>{template.nodes.length} nodi</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <Clock size={14} />
          <span>~5 min setup</span>
        </div>
      </div>
      
      {/* Tags */}
      <div className="flex flex-wrap gap-1">
        {template.tags.map(tag => (
          <span
            key={tag}
            className="px-2 py-1 bg-gray-700 text-xs text-gray-300 rounded-full"
          >
            #{tag}
          </span>
        ))}
      </div>
    </motion.div>
  );
  
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[9998]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[90vw] max-w-6xl max-h-[90vh]">
          <Dialog.Title className="sr-only">Template Gallery</Dialog.Title>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex"
          >
            {/* Main Content */}
            <div className="flex-1 flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Sparkles className="text-whatsapp-green" size={24} />
                    <h2 className="text-xl font-bold text-white">Template Automazioni</h2>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
                
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Cerca template..."
                    className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-whatsapp-green transition-colors"
                  />
                </div>
              </div>
              
              {/* Categories */}
              <div className="px-6 py-3 border-b border-gray-800 flex gap-2 overflow-x-auto">
                {templateCategories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-all ${
                      selectedCategory === category.id
                        ? 'bg-whatsapp-green text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <span>{category.icon}</span>
                    <span className="text-sm">{category.name}</span>
                  </button>
                ))}
              </div>
              
              {/* Templates Grid */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredTemplates.map(renderTemplateCard)}
                </div>
                
                {filteredTemplates.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-400">Nessun template trovato</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Detail Panel */}
            <AnimatePresence>
              {selectedTemplate && (
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: 400 }}
                  exit={{ width: 0 }}
                  className="border-l border-gray-800 bg-gray-850 overflow-hidden"
                >
                  <div className="w-[400px] h-full flex flex-col">
                    {/* Template Header */}
                    <div className="p-6 border-b border-gray-800">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-4xl">{selectedTemplate.icon}</span>
                          <div>
                            <h3 className="text-lg font-bold text-white">{selectedTemplate.name}</h3>
                            <p className="text-sm text-gray-400">{selectedTemplate.category}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedTemplate(null)}
                          className="text-gray-400 hover:text-white transition-colors"
                        >
                          <X size={20} />
                        </button>
                      </div>
                      <p className="text-gray-300">{selectedTemplate.description}</p>
                    </div>
                    
                    {/* Flow Preview */}
                    <div className="flex-1 p-6 overflow-y-auto">
                      <h4 className="text-white font-medium mb-3">Struttura Flusso</h4>
                      <div className="space-y-2">
                        {selectedTemplate.nodes.map((node, index) => (
                          <div
                            key={node.id}
                            className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg"
                          >
                            <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center text-xs text-gray-400">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-white">{node.data.label}</p>
                              <p className="text-xs text-gray-500 capitalize">{node.type}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Tags */}
                      <div className="mt-6">
                        <h4 className="text-white font-medium mb-3">Tags</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedTemplate.tags.map(tag => (
                            <span
                              key={tag}
                              className="px-3 py-1 bg-gray-800 text-sm text-gray-300 rounded-full flex items-center gap-1"
                            >
                              <Tag size={12} />
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="p-6 border-t border-gray-800 space-y-2">
                      <button
                        onClick={() => handleUseTemplate(selectedTemplate)}
                        className="w-full py-3 bg-whatsapp-green text-white rounded-lg hover:bg-whatsapp-dark transition-colors flex items-center justify-center gap-2"
                      >
                        <Play size={18} />
                        Usa Template
                      </button>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setPreviewMode(true)}
                          className="py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <Eye size={16} />
                          Anteprima
                        </button>
                        <button
                          onClick={() => handleDuplicate(selectedTemplate)}
                          className="py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <Copy size={16} />
                          Duplica
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

TemplateGallery.displayName = 'TemplateGallery';

export default TemplateGallery;
